import {
  Children,
  FC,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  cloneElement,
  isValidElement,
  useRef,
} from 'react';

import { cn } from 'utils/helpers';

interface PROPS extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

const Radio: FC<PROPS> = ({
  className,
  name,
  value,
  label,
  checked,
  disabled,
  ...props
}) => {
  const refInput = useRef<HTMLInputElement>(null);

  return (
    <label
      htmlFor={name}
      className={cn(
        'group relative inline-flex items-start text-sm leading-[20px] text-secondary-dark',
        className,
      )}
    >
      <input
        {...props}
        type="radio"
        defaultValue={value}
        name={name}
        ref={refInput}
        className={cn(
          'h-[14px] w-[14px]',
          'relative border-primary-base',
          'z-10 cursor-pointer opacity-0',
          'mr-[10px] mt-[2px]',
        )}
        disabled={disabled}
      />
      <div
        className={cn(
          'border group-hover:border-primary-base',
          'flex h-[14px] w-[14px] items-center justify-center',
          'absolute left-0 top-[2px] rounded-full',
          checked ? 'border-primary-base' : 'border-disabled',
          disabled && '!border-disabled bg-fill-secondary',
        )}
      >
        {checked && (
          <div
            className={cn(
              'h-[6px] w-[6px] rounded-full',
              disabled ? 'bg-disabled' : 'bg-primary-base',
            )}
          />
        )}
      </div>
      {label && (
        <span
          role="button"
          tabIndex={0}
          onKeyUp={() => null}
          className={cn('select-none', disabled && 'text-disabled')}
          onClick={() => refInput.current?.click()}
        >
          {label}
        </span>
      )}
    </label>
  );
};

interface _PROPS extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  groupClassName?: string;
  children: ReactNode;
  groupLabel?: string;
  labelSubText?: string;
  subText?: string;
  required?: boolean;
  textType?: 'default' | 'danger' | 'success' | 'warning';
}

export const RadioGroup: FC<_PROPS> = ({
  className,
  children,
  groupLabel,
  required,
  labelSubText = '(Optional)',
  subText,
  groupClassName,
  ...props
}) => (
  <div className={cn(props.textType, className)} {...props}>
    <div className={cn('flex items-center', 'mb-[10px]')}>
      {groupLabel && (
        <div className={cn('text-sm leading-5', 'block flex-1 font-semibold')}>
          {groupLabel}
          {required ? (
            <span
              className={cn(
                'text-sm font-semibold leading-5 text-danger',
                'ml-1 inline-block text-xs leading-[18px]',
              )}
            >
              *
            </span>
          ) : (
            <span className="ml-1 inline-block text-xs leading-[18px]">
              {labelSubText}
            </span>
          )}
        </div>
      )}
    </div>
    <div className={cn('gap-4', groupClassName)} role="group">
      {Children.map(children, (child) =>
        isValidElement<PROPS>(child)
          ? cloneElement(child, { ...child.props })
          : null,
      )}
    </div>
    {subText && <div className="input-subtext">{subText}</div>}
  </div>
);

export default Radio;
